import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, Clock, MapPin, Users, Tag, Heart, Share2, Plus, Minus } from 'lucide-react';
import { useCart } from '../components/CartContext';
import AddToCartModal from '../components/AddToCartModal';

const MULTI_DAY_EVENT_IDS = [4];
const SALES_CLOSED = true;


export default function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart, getCartTotal } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [addedItem, setAddedItem] = useState(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const contentRefs = useRef([]);
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadEvent() {
      try {
        const base = process.env.REACT_APP_API_BASE;

        const res = await fetch(`${base}/api/events/${id}`, {
          credentials: "include",
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const payload = await res.json();

        const e = payload.event ?? payload;

        const startDate = e.date ? new Date(e.date) : null;
        const endDate = startDate ? new Date(startDate) : null;
        if (endDate) endDate.setDate(startDate.getDate() + 1);

        const normalized = {
          id: e.id,
          title: e.title,
          subtitle: e.subtitle || "",
          date: startDate
            ? MULTI_DAY_EVENT_IDS.includes(e.id)
              ? `${startDate.getDate()} & ${endDate.getDate()} ${startDate.toLocaleString('en-SG', { month: 'long', year: 'numeric' })}`
              : startDate.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })
            : "",
          time: e.time || "",
          location: e.location || "",
          ticketPrice: Number(e.ticketPrice ?? e.ticket_price ?? 0),
          image: e.image || "",
          gallery: (e.gallery && e.gallery.length ? e.gallery : [e.image]).filter(Boolean),
          organizer: e.organizer || "NTU CAC Arts From The Heart",
          capacity: e.capacity || "",
          category: e.category || "Event",
          description: e.description || "",
          highlights: e.highlights || [],
          included: e.included || [],
        };

        setEvent(normalized);
      } catch (err) {
        console.error("Failed to load event:", err);
        navigate("/events");
      } finally {
        setLoading(false);
      }
    }

    loadEvent();
  }, [id, navigate]);

  // Scroll progress tracking
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = Math.min(scrollTop / docHeight, 1);
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Intersection Observer for fade-in animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("fade-in-visible");
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );

    contentRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      observer.disconnect();
    };
  }, [event]);

  const handleAddToCart = () => {
    const cartItem = {
      id: event.id,
      name: event.title,
      price: event.ticketPrice,
      quantity: quantity,
      image: event.image,
      type: 'ticket',
      eventDate: event.date
    };

    const wasAdded = addToCart(cartItem);

    if (wasAdded) {
      setAddedItem(cartItem);
      setIsModalOpen(true);
    }
  };

  const handleViewCart = () => {
    setIsModalOpen(false);
    navigate('/cart');
  };

  const increaseQuantity = () => setQuantity(prev => Math.min(prev + 1, 10));
  const decreaseQuantity = () => setQuantity(prev => Math.max(prev - 1, 1));

  if (loading) return <div className="p-8">Loading event...</div>;
  if (!event) return null;

  const totalPrice = (event.ticketPrice * quantity).toFixed(2);

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ backgroundColor: '#faf8f5' }}>
      {/* Background decorative elements - hidden on mobile */}
      <div className="hidden md:block absolute inset-0 pointer-events-none overflow-hidden opacity-30">
        {[...Array(12)].map((_, i) => (
          <div
            key={`art-${i}`}
            className="absolute"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              width: `${30 + Math.random() * 60}px`,
              height: `${30 + Math.random() * 60}px`,
              background: `linear-gradient(135deg, ${['#fbbf24', '#f9a8d4', '#a5b4fc', '#86efac'][i % 4]}, transparent)`,
              borderRadius: `${Math.random() * 50}%`,
              animation: `floatArt ${15 + Math.random() * 20}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 5}s`,
              opacity: 0.15,
            }}
          />
        ))}
      </div>

      <style>{`
        @keyframes floatArt {
          0%, 100% {
            transform: translate(0, 0) rotate(0deg) scale(1);
          }
          25% {
            transform: translate(30px, -30px) rotate(90deg) scale(1.1);
          }
          50% {
            transform: translate(0, -60px) rotate(180deg) scale(0.9);
          }
          75% {
            transform: translate(-30px, -30px) rotate(270deg) scale(1.05);
          }
        }

        @keyframes shimmer {
          0% {
            background-position: -200% center;
          }
          100% {
            background-position: 200% center;
          }
        }

        .shimmer-effect {
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
          background-size: 200% 100%;
          animation: shimmer 3s infinite;
        }

        .fade-in-section {
          opacity: 0;
          transform: translateY(30px);
          transition: opacity 0.6s ease-out, transform 0.6s ease-out;
        }

        .fade-in-visible {
          opacity: 1;
          transform: translateY(0);
        }

        .scroll-indicator {
          position: fixed;
          top: 0;
          left: 0;
          height: 3px;
          background: linear-gradient(90deg, #e55959, #ff8080);
          z-index: 9999;
          transition: width 0.1s ease-out;
          box-shadow: 0 0 10px rgba(229, 89, 89, 0.5);
        }

        @media (hover: none) {
          .hover\\:scale-105:hover {
            transform: none;
          }
        }
      `}</style>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 relative z-10">
        {/* Scroll Progress Indicator */}
        <div
          className="scroll-indicator"
          style={{ width: `${scrollProgress * 100}%` }}
        />

        <button
          onClick={() => navigate('/events')}
          className="mb-4 sm:mb-6 text-gray-600 hover:text-gray-900 transition-colors flex items-center gap-2 text-sm sm:text-base"
        >
          ← Back to Events
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
          {/* Left Column - Event Details */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            {/* Image Gallery */}
            <div className="relative group fade-in-section" ref={(el) => contentRefs.current[0] = el}>
              <div className="rounded-2xl sm:rounded-3xl overflow-hidden shadow-xl sm:shadow-2xl">
                <img
                  src={event.gallery[selectedImage]}
                  alt={event.title}
                  className="w-full h-[250px] sm:h-[350px] md:h-[400px] lg:h-[500px] object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="hidden sm:block absolute inset-0 shimmer-effect opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </div>

              {/* Gallery Thumbnails */}
              <div className="flex gap-2 sm:gap-3 mt-3 sm:mt-4 overflow-x-auto pb-2">
                {event.gallery.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImage(idx)}
                    className={`flex-shrink-0 rounded-lg sm:rounded-xl overflow-hidden transition-all duration-300 ${
                      selectedImage === idx
                        ? 'ring-2 sm:ring-4 ring-red-400 scale-105'
                        : 'opacity-60 hover:opacity-100'
                    }`}
                  >
                    <img
                      src={img}
                      alt={`Gallery ${idx + 1}`}
                      className="w-16 h-16 sm:w-20 sm:h-20 object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Event Info Card */}
            <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 shadow-lg fade-in-section" ref={(el) => contentRefs.current[1] = el}>
              <div className="flex items-start justify-between gap-3 mb-4">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-2 sm:mb-3">
                    <span className="inline-block bg-red-100 text-red-700 px-3 py-1 rounded-full text-xs sm:text-sm font-semibold">
                      {event.category}
                    </span>
                  </div>
                  <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
                    {event.title}
                  </h1>
                  <p className="text-base sm:text-lg md:text-xl text-gray-600">{event.subtitle}</p>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button className="p-2 sm:p-3 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors">
                    <Heart className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
                  </button>
                  <button className="p-2 sm:p-3 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors">
                    <Share2 className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
                  </button>
                </div>
              </div>

              {/* Event Details Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mt-4 sm:mt-6">
                <div className="flex items-start gap-3 p-3 sm:p-4 bg-red-50 rounded-xl">
                  <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-red-600 flex-shrink-0 mt-1" />
                  <div className="min-w-0">
                    <p className="text-xs sm:text-sm text-gray-600">Date</p>
                    <p className="font-semibold text-gray-900 text-sm sm:text-base">{event.date}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 sm:p-4 bg-pink-50 rounded-xl">
                  <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-pink-600 flex-shrink-0 mt-1" />
                  <div className="min-w-0">
                    <p className="text-xs sm:text-sm text-gray-600">Time</p>
                    <p className="font-semibold text-gray-900 text-sm sm:text-base">{event.time}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 sm:p-4 bg-blue-50 rounded-xl">
                  <MapPin className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 flex-shrink-0 mt-1" />
                  <div className="min-w-0">
                    <p className="text-xs sm:text-sm text-gray-600">Location</p>
                    <p className="font-semibold text-gray-900 text-sm sm:text-base">{event.location}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 sm:p-4 bg-green-50 rounded-xl">
                  <Users className="w-5 h-5 sm:w-6 sm:h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div className="min-w-0">
                    <p className="text-xs sm:text-sm text-gray-600">Capacity</p>
                    <p className="font-semibold text-gray-900 text-sm sm:text-base">{event.capacity}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* About Section */}
            <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 shadow-lg fade-in-section" ref={(el) => contentRefs.current[2] = el}>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">About This Event</h2>
              <p className="text-gray-700 whitespace-pre-line leading-relaxed text-sm sm:text-base">
                {event.description}
              </p>
            </div>

            {/* Mobile Booking Card - Shows only on mobile below content */}
            <div className="lg:hidden">
              <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden">
                {/* Header */}
                <div className={`p-4 sm:p-6 text-white ${SALES_CLOSED ? 'bg-gradient-to-r from-gray-400 to-gray-500' : 'bg-gradient-to-r from-red-400 via-red-500 to-orange-500'}`}>
                  {SALES_CLOSED ? (
                    <>
                      <span className="text-xl sm:text-2xl font-bold">Ticket Sales Closed</span>
                      <p className="text-gray-200 mt-2 text-sm sm:text-base">This event is no longer accepting registrations</p>
                    </>
                  ) : (
                    <>
                      <div className="flex items-baseline gap-2">
                        <span className="text-3xl sm:text-4xl font-bold">${event.ticketPrice}</span>
                        <span className="text-base sm:text-lg opacity-90">per ticket</span>
                      </div>
                      <p className="text-red-100 mt-2 text-sm sm:text-base">General Admission</p>
                    </>
                  )}
                </div>

                <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
                  {!SALES_CLOSED && (
                    <>
                      {/* Quantity Selector */}
                      <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-4">
                        <label className="text-gray-700 font-medium block mb-3 text-sm sm:text-base">Number of Tickets</label>
                        <div className="flex items-center justify-center gap-4">
                          <button
                            onClick={decreaseQuantity}
                            className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white shadow-md flex items-center justify-center hover:bg-gray-50 transition-colors active:scale-95"
                            disabled={quantity <= 1}
                          >
                            <Minus className="w-5 h-5 sm:w-6 sm:h-6 text-gray-700" />
                          </button>
                          <span className="text-2xl sm:text-3xl font-bold text-gray-900 min-w-[3rem] text-center">
                            {quantity}
                          </span>
                          <button
                            onClick={increaseQuantity}
                            className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white shadow-md flex items-center justify-center hover:bg-gray-50 transition-colors active:scale-95"
                            disabled={quantity >= 10}
                          >
                            <Plus className="w-5 h-5 sm:w-6 sm:h-6 text-gray-700" />
                          </button>
                        </div>
                        <p className="text-xs sm:text-sm text-gray-600 mt-3 text-center">Maximum 10 tickets per person</p>
                      </div>

                      {/* Total Price */}
                      <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-xl p-4">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-700 font-medium text-sm sm:text-base">Total Price</span>
                          <span className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-red-600 to-orange-600 bg-clip-text text-transparent">
                            ${totalPrice}
                          </span>
                        </div>
                      </div>
                    </>
                  )}

                  <button
                    onClick={handleAddToCart}
                    disabled={SALES_CLOSED}
                    className="w-full text-white py-3 sm:py-4 rounded-xl font-bold text-base sm:text-lg transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                    style={{
                      background: SALES_CLOSED
                        ? 'linear-gradient(135deg, #9ca3af 0%, #d1d5db 100%)'
                        : 'linear-gradient(135deg, #e55959 0%, #ff8080 100%)',
                      boxShadow: SALES_CLOSED ? 'none' : '0 4px 20px rgba(229, 89, 89, 0.3)'
                    }}
                  >
                    <Tag className="w-4 h-4 sm:w-5 sm:h-5" />
                    {SALES_CLOSED ? 'Unavailable' : 'Add to Cart'}
                  </button>
                </div>
              </div>

              <div className="mt-4 sm:mt-6 bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-lg">
                <h3 className="font-bold text-gray-900 mb-2 sm:mb-3 text-sm sm:text-base">Organized by</h3>
                <p className="font-semibold mb-2 text-sm sm:text-base" style={{ color: '#e55959' }}>{event.organizer}</p>
                <p className="text-xs sm:text-sm text-gray-600">
                  Supporting communities through arts and culture since 2004
                </p>
              </div>
            </div>
          </div>

          {/* Right Column - Ticket Booking Card (Desktop Only) */}
          <div className="hidden lg:block lg:col-span-1">
            <div className="sticky top-8">
              <div className="bg-white rounded-3xl shadow-2xl overflow-hidden transform transition-all duration-500 hover:shadow-3xl">
                {/* Header */}
                <div className={`p-6 text-white ${SALES_CLOSED ? 'bg-gradient-to-r from-gray-400 to-gray-500' : 'bg-gradient-to-r from-red-400 via-red-500 to-orange-500'}`}>
                {SALES_CLOSED ? (
                  <>
                    <span className="text-2xl font-bold text-center block">Ticket Sales Closed</span>
                    <p className="text-gray-200 mt-2 text-center">This event is no longer accepting registrations</p>
                  </>
                  ) : (
                    <>
                      <div className="flex items-baseline gap-2">
                        <span className="text-4xl font-bold">${event.ticketPrice}</span>
                        <span className="text-lg opacity-90">per ticket</span>
                      </div>
                      <p className="text-red-100 mt-2">General Admission</p>
                    </>
                  )}
                </div>

                <div className="p-6 space-y-6">
                  {!SALES_CLOSED && (
                    <>
                      {/* Quantity Selector */}
                      <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-4">
                        <label className="text-gray-700 font-medium block mb-3">Number of Tickets</label>
                        <div className="flex items-center justify-center gap-4">
                          <button
                            onClick={decreaseQuantity}
                            className="w-12 h-12 rounded-full bg-white shadow-md flex items-center justify-center hover:bg-gray-50 transition-colors active:scale-95"
                            disabled={quantity <= 1}
                          >
                            <Minus className="w-6 h-6 text-gray-700" />
                          </button>
                          <span className="text-3xl font-bold text-gray-900 min-w-[3rem] text-center">
                            {quantity}
                          </span>
                          <button
                            onClick={increaseQuantity}
                            className="w-12 h-12 rounded-full bg-white shadow-md flex items-center justify-center hover:bg-gray-50 transition-colors active:scale-95"
                            disabled={quantity >= 10}
                          >
                            <Plus className="w-6 h-6 text-gray-700" />
                          </button>
                        </div>
                        <p className="text-sm text-gray-600 mt-3 text-center">Maximum 10 tickets per person</p>
                      </div>

                      {/* Total Price */}
                      <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-xl p-4">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-700 font-medium">Total Price</span>
                          <span className="text-3xl font-bold bg-gradient-to-r from-red-600 to-orange-600 bg-clip-text text-transparent">
                            ${totalPrice}
                          </span>
                        </div>
                      </div>
                    </>
                  )}

                  <button
                    onClick={handleAddToCart}
                    disabled={SALES_CLOSED}
                    className="w-full text-white py-4 rounded-xl font-bold text-lg transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                    style={{
                      background: SALES_CLOSED
                        ? 'linear-gradient(135deg, #9ca3af 0%, #d1d5db 100%)'
                        : 'linear-gradient(135deg, #e55959 0%, #ff8080 100%)',
                      boxShadow: SALES_CLOSED ? 'none' : '0 4px 20px rgba(229, 89, 89, 0.3)'
                    }}
                  >
                    <Tag className="w-5 h-5" />
                    {SALES_CLOSED ? 'Registration has ended' : 'Add to Cart'}
                  </button>
                </div>
              </div>

              <div className="mt-6 bg-white rounded-3xl p-6 shadow-lg fade-in-section" ref={(el) => contentRefs.current[5] = el}>
                <h3 className="font-bold text-gray-900 mb-3">Organized by</h3>
                <p className="font-semibold mb-2" style={{ color: '#e55959' }}>{event.organizer}</p>
                <p className="text-sm text-gray-600">
                  Supporting communities through arts and culture since 2004
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add to Cart Modal */}
      <AddToCartModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        item={addedItem}
        cartTotal={getCartTotal()}
        onViewCart={handleViewCart}
      />
    </div>
  );
}